// src/FileUploader.jsx
import React, { useState } from 'react';
import ExcelJS from 'exceljs';

const fbCode = (v) => {
  const s = String(
    v && typeof v === 'object'
      ? ('text' in v ? v.text : ('result' in v ? v.result : v))
      : (v ?? '')
  ).trim();
  const m = s.match(/\d+/);
  return m ? parseInt(m[0], 10) : NaN;
};

// Robust numeric parser (spaces, apostrophes, thousands, decimal comma/dot)
const parseNumber = (v) => {
  if (v == null) return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'object') {
    if ('result' in v) return parseNumber(v.result);
    if ('text'   in v) return parseNumber(v.text);
    if (Array.isArray(v.richText)) return parseNumber(v.richText.map(t => t.text).join(''));
  }
  let s = String(v)
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, '')
    .replace(/['’]/g, '');
  const ci = s.lastIndexOf(',');
  const di = s.lastIndexOf('.');
  if (ci !== -1 && di !== -1) {
    if (ci > di) s = s.replace(/\./g, '').replace(',', '.');
    else         s = s.replace(/,/g, '');
  } else if (ci !== -1) {
    s = s.replace(',', '.');
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

// meters → millimeters (rounded)
const toMm = (nMeters) => {
  const n = parseNumber(nMeters);
  return n > 0 ? Math.round(n * 1000) : 0;
};

// Color map & extractor from MATNR (last 3 chars → 2 color letters)
const colorMap = {
  R: 'FF0000', G: 'FFFF00', I: '00FF00', H: '808080',
  P: 'FFC0CB', C: 'A52A2A', B: '0000FF', S: '000000',
  W: 'FFFFFF', X: null
};
function getColors(matnr) {
  if (!matnr) return [null, null];
  const s = String(matnr).slice(-3);
  const f = s[0], s2 = s[1];
  const c1 = colorMap[f];
  const c2 = colorMap[s2] || c1;
  return [c1, s2 === 'X' ? c1 : c2];
}

export default function FileUploader({ onDataReady }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');

  const onFileChange = (e) => {
    const f = e.target.files[0];
    setFile(f || null);
    setName(f ? f.name : '');
  };

  const loadAndProcess = async (e) => {
    e.preventDefault();
    if (!file) {
      alert('Select an .xlsx first');
      return;
    }

    console.log('📥 Loading file:', name);

    // 1) Load workbook & first sheet
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(await file.arrayBuffer());
    const ws = wb.worksheets[0];

    // 2) Map headers → column indices (exact text of row 1)
    const H = {};
    ws.getRow(1).eachCell((cell, idx) => {
      const t = (cell.value || '').toString().trim();
      if (t) H[t] = idx;
    });
    console.log('🔠 Header map:', H);

    // 3) Read raw rows + remember original row number (RN)
    const raw = [];
    ws.eachRow({ includeEmpty: false }, (row, rn) => {
      if (rn === 1) return;
      const idxNm = H['NMENGE'] || H['MENGE'];
      const idxFb = H['KSTST'] || H['FB']; // prefer KSTST over FB
      raw.push({
        RN:       rn, // ← keep original order from sheet
        FPNR:     row.getCell(H['FPNR']).value,
        KABEL:    row.getCell(H['KABEL']).value,
        MATNR:    row.getCell(H['MATNR']).value,
        MENGE:    row.getCell(idxNm)?.value,
        KSTST:    fbCode(row.getCell(idxFb).value),
        DPG:      row.getCell(H['DPG'])?.value,
        POSITION: row.getCell(H['POSITION'] || H['TEXT1'])?.value,
        KAMMERNR: row.getCell(H['KAMMERNR'] || H['BMNR'])?.value,
      });
    });
    console.log('🔍 Raw rows:', raw);

    // ====== Diagnostics (collapsed) ======
    const k3211     = raw.filter(r => r.KSTST === 3211);
    const zeroMenge = raw.filter(r => r.KSTST === 3211 && parseNumber(r.MENGE) === 0);
    console.groupCollapsed('🐞 Diagnostics');
    console.table({
      total_rows: raw.length,
      kstst_3211: k3211.length,
      kstst_3211_menge_zero: zeroMenge.length,
    });
    console.groupEnd();

    // 4) Filter ONLY KSTST == 3211, then group by cable
    const byCable = {};
    k3211.forEach(r => {
      const key = r.KABEL;
      (byCable[key] ||= []).push(r);
    });

    // 5) Sort each group by POSITION for side fields, but we’ll
    //    also keep an RN-sorted copy to pick the FIRST MENGE per cable.
    Object.values(byCable).forEach(grp =>
      grp.sort((a, b) => (String(a.POSITION || '')).localeCompare(String(b.POSITION || '')))
    );

    // 6) Build normalized rows
    const builtRows = [];
    Object.entries(byCable).forEach(([cable, grp]) => {
      // FIRST row in original sheet for this cable (by RN)
      const orderedByRN = [...grp].sort((a, b) => a.RN - b.RN);
      const firstRow    = orderedByRN[0];
      // If the first has no/zero MENGE, take the first non-zero by RN
      const nzRow       = orderedByRN.find(r => parseNumber(r.MENGE) > 0) || firstRow;

      // Length = FIRST MENGE seen for the cable (meters → mm)
      const lengthMm    = toMm(nzRow.MENGE);

      // Use the first row to drive main material/colors (matches how workbook looks)
      const mainMat     = String(firstRow.MATNR || '');
      const [c1, c2]    = getColors(mainMat);
      const dpg         = firstRow.DPG || '';

      const allMats  = grp.map(r => r.MATNR).filter(Boolean).map(String);

      // side 1
      const cos1Mat = [...allMats].reverse().find(m => m.startsWith('A')) || '';
      const tul1Mat = allMats.find(m => m.startsWith('T')) || '';
      const cos1Row = grp.find(r => String(r.MATNR) === cos1Mat) || {};
      const pos1    = cos1Row.POSITION || '';
      const kam1    = cos1Row.KAMMERNR || '';

      // side 2
      const mat2    = allMats.find(m => m.startsWith('G')) || '';
      const cos2Mat = allMats.find(m => m.startsWith('A') && m !== cos1Mat) || '';
      const tul2Mat = tul1Mat;
      const pos2Row = grp.find(r => r.POSITION && r.POSITION !== firstRow.POSITION) || {};
      const pos2    = pos2Row.POSITION || '';
      const kam2    = pos2Row.KAMMERNR || '';

      // Debug: confirm what we use for length per cable
      console.debug('📏 Cable length pick', { cable, firstRN:firstRow.RN, firstMENGE:firstRow.MENGE, nzMENGE:nzRow.MENGE, lengthMm });

      builtRows.push({
        'Pos Nr. (Côté 1)':  pos1,
        'DPG (Côté 1)':      dpg,
        'Matériel (Côté 1)': '',
        'Kam (Côté 1)':      kam1,
        'Cosse (Côté 1)':    cos1Mat,
        'Tulle (Côté 1)':    '',

        'Câble':             cable,
        'Matériel':          mainMat,
        'section':           0.35,
        'Longueur (mm)':     lengthMm,

        'Couleur 1':         c1,
        'Couleur 2':         c2,

        'Pos Nr. (Côté 2)':  pos2,
        'Matériel (Côté 2)': mat2,
        'Kam (Côté 2)':      kam2,
        'Cosse (Côté 2)':    cos2Mat,
        'Tulle (Côté 2)':    tul2Mat,
      });
    });
    console.log('🚧 builtRows before swap:', builtRows);

    if (!builtRows.length) {
      alert('No data matched your filters (KSTST==3211).');
      return;
    }

    // 7) Remarque from unique "Matériel (Côté 2)"
    const allMat2    = builtRows.map(r => r['Matériel (Côté 2)']).filter(Boolean);
    const uniqueMat2 = Array.from(new Set(allMat2));
    const remarque   = uniqueMat2.join(', ');
    console.log('📝 header.remarque =', remarque);

    // 8) Special swap for specific Cosse (Côté 2)
    const SWAP_CODES = ['A3205701', 'A3212504', 'A3212503'];
    const rowsFixed = builtRows.map(r => {
      const cosse2 = r['Cosse (Côté 2)']?.trim();
      if (SWAP_CODES.includes(cosse2)) {
        const nr = { ...r };
        [nr['Pos Nr. (Côté 1)'], nr['Pos Nr. (Côté 2)']] =
          [r['Pos Nr. (Côté 2)'], r['Pos Nr. (Côté 1)']];
        [nr['Cosse (Côté 1)'], nr['Cosse (Côté 2)']] =
          [r['Cosse (Côté 2)'], r['Cosse (Côté 1)']];
        return nr;
      }
      return r;
    });
    console.log('✅ rowsFixed after swap:', rowsFixed);

    // 9) Derive module & stand from first matched row’s FPNR
    const sampleRow = k3211[0] || raw[0] || { FPNR: '' };
    const fp = (sampleRow.FPNR ?? '').toString();
    const moduleCode = fp.slice(0, -3);
    const stand = fp.slice(-3);

    // 10) Push to parent
    onDataReady({
      header: { module: moduleCode, stand, qs: '', remarque },
      rows: rowsFixed,
      sectionRows: [{ code: '', length: '3.20 (+0.20 / -0.20)' }]
    });
  };

  return (
    <form onSubmit={loadAndProcess}>
      <input type="file" accept=".xlsx" onChange={onFileChange} />
      {name && <span style={{ marginLeft: 8 }}>{name}</span>}
      <button type="submit" style={{ marginLeft: 12 }}>
        Load &amp; Preview
      </button>
    </form>
  );
}
